import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { billingApi } from "@/services/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const MAX_TRIES = 8;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    async function poll() {
      if (cancelled) return;
      tries.current += 1;
      try {
        const res = await billingApi.status(sessionId);
        const d = res?.data || res || {};
        if (d.payment_status === "paid") { setInfo(d); setState("paid"); return; }
        if (tries.current >= MAX_TRIES) { setState("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (tries.current >= MAX_TRIES) { setState("error"); return; }
        setTimeout(poll, 2000);
      }
    }
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-lg items-center justify-center p-6" data-testid="payment-success-page">
      <Card className="w-full p-8 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
            <p className="mt-4 text-lg font-semibold text-slate-900">Confirming your payment…</p>
            <p className="mt-1 text-sm text-slate-500">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" data-testid="payment-success-ok" />
            <p className="mt-4 text-lg font-bold text-slate-900">You're subscribed!</p>
            <p className="mt-1 text-sm text-slate-600">Your {info?.tier ? `${info.tier} ` : ""}plan is now active{info?.max_doors ? ` with up to ${info.max_doors} doors` : ""}. Your whole team has been upgraded.</p>
            <Button className="mt-6" onClick={() => navigate("/settings?tab=subscription")} data-testid="payment-success-continue">Go to subscription</Button>
          </>
        )}
        {state === "timeout" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-amber-500" />
            <p className="mt-4 text-lg font-semibold text-slate-900">Payment is processing</p>
            <p className="mt-1 text-sm text-slate-600">Your payment went through and your plan will activate shortly. You can safely return to your subscription.</p>
            <Button className="mt-6" variant="outline" onClick={() => navigate("/settings?tab=subscription")}>Back to subscription</Button>
          </>
        )}
        {state === "error" && (
          <>
            <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
            <p className="mt-4 text-lg font-semibold text-slate-900">We couldn't confirm this payment</p>
            <p className="mt-1 text-sm text-slate-600">If you completed checkout, your plan will still activate. Check your subscription in a minute.</p>
            <Button className="mt-6" variant="outline" onClick={() => navigate("/settings?tab=subscription")}>Back to subscription</Button>
          </>
        )}
      </Card>
    </div>
  );
}
